import { useState } from "react";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { FRANCHISE_BENEFITS, STORIES_NUMBERS } from "../data/content";

export default function Franchise() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", interest: "", capital: "", message: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: "", email: "", phone: "", city: "", interest: "", capital: "", message: "" });
  };

  return (
    <div data-testid="franchise-page" className="pt-28">
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Partner with us</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1]">
            Bring Stories to <em className="italic text-[#C76D4B]">your city.</em>
          </h1>
          <p className="mt-8 text-[#7A5C45] max-w-2xl mx-auto">
            We're partnering with hands-on operators across India to scale conversation-first
            cafés. Build something meaningful — backed by EBG Group.
          </p>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="px-5 sm:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6 bg-[#F3EBE1] rounded-[2rem] p-8 sm:p-10">
          {STORIES_NUMBERS.map((n, i) => (
            <div key={i} data-testid={`franchise-stat-${i}`}>
              <div className="font-serif-display text-4xl sm:text-5xl text-[#C76D4B]">{n.value}</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#7A5C45] mt-2">{n.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Why Stories</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5">
              A partnership that <em className="italic">travels well.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {FRANCHISE_BENEFITS.map((b, i) => (
              <div key={b.title} data-testid={`franchise-benefit-${i}`} className="bg-white border border-[#E8D8C8] rounded-3xl p-7 card-lift">
                <div className="h-10 w-10 rounded-full bg-[#F3EBE1] grid place-items-center">
                  <Sparkles size={16} className="text-[#C76D4B]" />
                </div>
                <h3 className="font-serif-display text-xl mt-5">{b.title}</h3>
                <p className="text-sm text-[#7A5C45] mt-3 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO FORMATS */}
      <section className="py-20 sm:py-24 bg-[#2A1E17] text-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[
            { t: "Stories Café", d: "Compact, high-turn format for high streets, college zones and lifestyle hubs.", a: "1,200 — 1,800 sq.ft", b: "40 — 60 covers" },
            { t: "Stories Café Lounge", d: "Larger, community-led format for premium neighbourhoods and business districts.", a: "2,000 — 3,000 sq.ft", b: "70 — 100 covers" },
          ].map((f, i) => (
            <div key={f.t} data-testid={`format-${i}`} className="border border-white/15 rounded-[2rem] p-8 sm:p-10">
              <div className="text-xs tracking-[0.22em] uppercase text-[#D4A373]">Format 0{i + 1}</div>
              <h3 className="font-serif-display text-4xl mt-3">{f.t}</h3>
              <p className="text-sm text-[#FAF7F2]/75 mt-4">{f.d}</p>
              <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-white/10">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#D4A373]">Footprint</div>
                  <div className="font-serif-display text-2xl mt-2">{f.a}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#D4A373]">Capacity</div>
                  <div className="font-serif-display text-2xl mt-2">{f.b}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Enquire</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-4">
              Let's start a <em className="italic">conversation.</em>
            </h2>
          </div>
          <form
            onSubmit={onSubmit}
            data-testid="franchise-form"
            className="bg-[#F3EBE1] rounded-[2rem] p-8 sm:p-12"
          >
            {submitted && (
              <div data-testid="franchise-form-success" className="mb-6 flex items-center gap-3 bg-[#7B8B6F]/15 border border-[#7B8B6F]/35 text-[#4A3320] px-5 py-4 rounded-2xl">
                <Check size={18} className="text-[#7B8B6F]" />
                <span className="text-sm">Thanks — your franchise enquiry has been noted. Our partnerships team will reach out within 48 hours.</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Full name</label>
                <input data-testid="franchise-name" required className="input-line" value={form.name} onChange={update("name")} placeholder="Your full name" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Email</label>
                <input data-testid="franchise-email" required type="email" className="input-line" value={form.email} onChange={update("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Phone</label>
                <input data-testid="franchise-phone" required className="input-line" value={form.phone} onChange={update("phone")} placeholder="+91 ..." />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">City / Location of interest</label>
                <input data-testid="franchise-city" className="input-line" value={form.city} onChange={update("city")} placeholder="Hyderabad · Jubilee Hills" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Format of interest</label>
                <input data-testid="franchise-format" className="input-line" value={form.interest} onChange={update("interest")} placeholder="Café / Café Lounge" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Capital available (₹)</label>
                <input data-testid="franchise-capital" className="input-line" value={form.capital} onChange={update("capital")} placeholder="₹ 80L — 1.5Cr" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#7A5C45]">Message</label>
                <textarea data-testid="franchise-message" className="input-line min-h-[120px] resize-none" value={form.message} onChange={update("message")} placeholder="Tell us about your background and why you'd like to partner with Stories." />
              </div>
            </div>
            <button type="submit" className="btn-primary mt-10" data-testid="franchise-submit">
              Submit Enquiry <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
